import React, { useState } from "react";
import { Form, Formik, FormikConfig } from "formik";
import { object, string } from "yup";
import classNames from "classnames";
import { Col, Row } from "react-bootstrap";
import { submitHelper } from "@helpers/form";
import { Organization } from "@common/types/organization";
import { User } from "@common/types/user";
import { useUpdateUserMutation } from "@mutations/user";
import { useUpdateOrganizationMutation } from "@api/organization.api";
import animation from "../layout/OnboardingLayout.module.scss";
import { FormRow } from "../../forms/FormRow";
import { ButtonWithLoader } from "../../general/ButtonWithLoader/ButtonWithLoader";
import { KeyboardReturnIcon } from "../../../icons";
import { KeyboardShortcut } from "../../general/KeyboardShortcut/KeyboardShortcut";
import styles from "./Profile.module.scss";
import commonStyles from "./common.module.scss";

interface FormSchema {
    firstName: string;
    lastName: string;
    organizationName: string;
}

interface Props {
    organization: Organization;
    user: User;
    onSubmit: () => void;
}

export const OrganizationProfile: React.FC<Props> = ({
    organization,
    user,
    onSubmit,
}) => {
    const [loading, setLoading] = useState(false);
    const updateUserMutation = useUpdateUserMutation();
    const updateOrganizationMutation = useUpdateOrganizationMutation();

    const form: FormikConfig<FormSchema> = {
        initialValues: {
            firstName: user.firstName ?? "",
            lastName: user.lastName ?? "",
            organizationName: organization.name ?? "",
        },
        validationSchema: object().shape({
            firstName: string().required("First name is required"),
            lastName: string().required("Last name is required"),
            organizationName: string().required(
                "Organization name is required",
            ),
        }),
        onSubmit: submitHelper({
            loading,
            setLoading,
            handler: async ({ firstName, lastName, organizationName }) => {
                await updateUserMutation.mutateAsync({
                    preferredName: `${firstName} ${lastName}`,
                });

                await updateOrganizationMutation.mutateAsync({
                    organizationId: organization.id,
                    payload: { name: organizationName },
                });

                onSubmit();
            },
        }),
    };

    return (
        <div className={commonStyles.content420}>
            <header className={classNames(animation.fadeIn2)}>
                <h1 className={classNames(commonStyles.title, styles.header)}>
                    Apply to automate your accounting
                </h1>
            </header>

            <Formik {...form}>
                {({ isValid }) => (
                    <Form className={classNames(animation.fadeIn1)}>
                        <Row>
                            <Col>
                                <FormRow
                                    fieldName="firstName"
                                    label="First name"
                                    size="sm"
                                />
                            </Col>

                            <Col>
                                <FormRow
                                    fieldName="lastName"
                                    label="Last name"
                                    size="sm"
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <FormRow
                                    fieldName="organizationName"
                                    label="Organization name"
                                    size="sm"
                                />
                            </Col>
                        </Row>

                        <footer className="mt-4">
                            <ButtonWithLoader
                                type="submit"
                                variant="secondary"
                                loading={loading}
                                block
                                disabled={!isValid}
                            >
                                Create organization
                                <KeyboardShortcut square className="ml-2">
                                    <KeyboardReturnIcon />
                                </KeyboardShortcut>
                            </ButtonWithLoader>
                        </footer>
                    </Form>
                )}
            </Formik>
        </div>
    );
};
